import React from 'react';
import { Box, Typography, Paper } from '@mui/material';

export interface TooltipData {
  layerId: string;
  screenX: number;
  screenY: number;
  // From Float32Array: lon, lat, depthOrAlt, value
  lon?: number;
  lat?: number;
  depthOrAlt?: number;   // negative = depth in meters (earthquakes), positive = altitude (fires)
  value?: number;        // normalized [0,1]
  // Derived display fields
  label?: string;        // pre-formatted label from C++ (e.g. "Mag 5.3 depth 42km")
}

interface HazardTooltipProps {
  data: TooltipData;
  onDismiss?: () => void;
}

const HazardTooltip: React.FC<HazardTooltipProps> = ({ data, onDismiss }) => {
  // Position tooltip near click, shifted right+down to avoid cursor overlap
  const OFFSET_X = 12;
  const OFFSET_Y = 12;

  const renderContent = () => {
    if (data.label) {
      // Use pre-formatted label from C++ if available
      return (
        <Typography
          variant="body2"
          sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.8rem', fontFamily: 'monospace' }}
        >
          {data.label}
        </Typography>
      );
    }

    if (data.layerId === 'aircraft') {
      const speedKts = data.value !== undefined
        ? Math.round(data.value * 250 * 1.944)
        : 'N/A';
      const altFt = data.depthOrAlt !== undefined
        ? Math.round(data.depthOrAlt * 3.28084)
        : 'N/A';
      return (
        <Box>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', display: 'block', mb: 0.25 }}
          >
            AIRCRAFT
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'rgba(255,255,255,0.95)', fontSize: '0.82rem', fontWeight: 600 }}
          >
            {altFt} ft
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', display: 'block' }}
          >
            {speedKts} kts
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.68rem', fontFamily: 'monospace', display: 'block' }}
          >
            {data.label ?? ''}
          </Typography>
        </Box>
      );
    }

    if (data.layerId === 'earthquakes') {
      const magnitude = data.value !== undefined ? (data.value * 9.0).toFixed(1) : 'N/A';
      const depthKm = data.depthOrAlt !== undefined
        ? Math.round(Math.abs(data.depthOrAlt) / 1000)
        : 'N/A';
      const latStr = data.lat !== undefined ? data.lat.toFixed(3) : 'N/A';
      const lonStr = data.lon !== undefined ? data.lon.toFixed(3) : 'N/A';

      return (
        <Box>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', display: 'block', mb: 0.25 }}
          >
            EARTHQUAKE
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'rgba(255,255,255,0.95)', fontSize: '0.82rem', fontWeight: 600 }}
          >
            Magnitude {magnitude}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', display: 'block' }}
          >
            Depth: {depthKm} km
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.68rem', fontFamily: 'monospace', display: 'block' }}
          >
            {latStr}, {lonStr}
          </Typography>
        </Box>
      );
    }

    if (data.layerId === 'fires') {
      const frpMW = data.value !== undefined ? Math.round(data.value * 1000) : 'N/A';
      const latStr = data.lat !== undefined ? data.lat.toFixed(3) : 'N/A';
      const lonStr = data.lon !== undefined ? data.lon.toFixed(3) : 'N/A';

      return (
        <Box>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', display: 'block', mb: 0.25 }}
          >
            WILDFIRE HOTSPOT
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'rgba(255,255,255,0.95)', fontSize: '0.82rem', fontWeight: 600 }}
          >
            FRP: {frpMW} MW
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.68rem', fontFamily: 'monospace', display: 'block' }}
          >
            {latStr}, {lonStr}
          </Typography>
        </Box>
      );
    }

    return (
      <Typography
        variant="body2"
        sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.8rem' }}
      >
        {data.label ?? data.layerId}
      </Typography>
    );
  };

  return (
    <Box
      onClick={onDismiss}
      sx={{
        position: 'absolute',
        left: data.screenX + OFFSET_X,
        top: data.screenY + OFFSET_Y,
        zIndex: 100,
        pointerEvents: 'auto',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          background: 'rgba(10, 10, 20, 0.85)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: '8px',
          padding: '8px 12px',
          maxWidth: 200,
          cursor: 'pointer',
        }}
        title="Click to dismiss"
      >
        {renderContent()}
      </Paper>
    </Box>
  );
};

export default HazardTooltip;
